import { useState } from "react";
import { Search, ExternalLink, ArrowLeftRight, Plus, X } from "lucide-react";
import { googleFlightsUrl } from "../lib/googleFlights";
import SaveRouteToTrip from "./travel/SaveRouteToTrip";

/* Route search — opens Google Flights pre-filled (no API key, always current).
   Shared by the Home page (#find-flights anchor) and Flight Smart Search. */
const TYPES = [["round", "Round Trip"], ["oneway", "One Way"], ["multi", "Multi City"]];
const inp2 = "w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:border-blue-600 transition-colors bg-white";

export const GoogleFlightsCard = ({ compact = false }) => {
  const [type, setType] = useState("round");
  const [f, setF] = useState({ from: "", to: "", depart: "", ret: "", cabin: "economy", passengers: 1 });
  const [legs, setLegs] = useState([{ from: "", to: "", date: "" }, { from: "", to: "", date: "" }]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setLeg = (i, k, v) => setLegs((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const go = (e) => {
    e.preventDefault();
    if (type === "multi") {
      if (!legs.some((l) => l.from.trim() && l.to.trim())) return;
      window.open(googleFlightsUrl({ type, legs, cabin: f.cabin, passengers: f.passengers }), "_blank", "noopener");
      return;
    }
    if (!f.from.trim() || !f.to.trim()) return;
    window.open(googleFlightsUrl({ ...f, type, from: f.from.trim(), to: f.to.trim() }), "_blank", "noopener");
  };

  return (
    <form onSubmit={go} id="find-flights" style={{ scrollMarginTop: "90px" }}
      className={`border border-blue-200 bg-blue-50/40 p-5 ${compact ? "" : "mb-10"}`} data-testid="google-flights-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading font-900 text-lg text-blue-800 flex items-center gap-2">
          <Search className="w-5 h-5" /> Find &amp; compare flights
        </h2>
        <div className="flex gap-1" data-testid="gf-type-tabs">
          {TYPES.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setType(v)} data-testid={`gf-type-${v}`}
              className={`px-3 py-1.5 text-xs font-800 rounded-full border transition-colors ${type === v
                ? "border-blue-700 bg-blue-700 text-white" : "border-zinc-300 bg-white text-zinc-600 hover:border-blue-500"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1 mb-4">
        Search a route and we'll open <b>Google Flights</b> pre-filled in a new tab — live prices across airlines.
      </p>

      {type !== "multi" ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2.5">
          <input value={f.from} onChange={(e) => set("from", e.target.value)} placeholder="From (city or airport)"
            className={`${inp2} lg:col-span-2`} data-testid="gf-from" required />
          <input value={f.to} onChange={(e) => set("to", e.target.value)} placeholder="To (city or airport)"
            className={`${inp2} lg:col-span-2`} data-testid="gf-to" required />
          <input type="date" value={f.depart} onChange={(e) => set("depart", e.target.value)} className={inp2}
            data-testid="gf-depart" title="Departure date" />
          {type === "round" ? (
            <input type="date" value={f.ret} onChange={(e) => set("ret", e.target.value)} className={inp2}
              min={f.depart || undefined} data-testid="gf-return" title="Return date" />
          ) : <div className="hidden lg:block" />}
        </div>
      ) : (
        <div className="space-y-2" data-testid="gf-multi-legs">
          {legs.map((l, i) => (
            <div key={i} className="grid sm:grid-cols-2 lg:grid-cols-7 gap-2.5 items-center">
              <span className="hidden lg:flex items-center justify-center text-xs font-800 text-blue-700">Leg {i + 1}</span>
              <input value={l.from} onChange={(e) => setLeg(i, "from", e.target.value)} placeholder={`From (leg ${i + 1})`}
                className={`${inp2} lg:col-span-2`} data-testid={`gf-leg-from-${i}`} required={i < 2} />
              <input value={l.to} onChange={(e) => setLeg(i, "to", e.target.value)} placeholder="To"
                className={`${inp2} lg:col-span-2`} data-testid={`gf-leg-to-${i}`} required={i < 2} />
              <input type="date" value={l.date} onChange={(e) => setLeg(i, "date", e.target.value)} className={`${inp2} lg:col-span-1`}
                data-testid={`gf-leg-date-${i}`} />
              <div className="flex justify-start lg:justify-center">
                {i >= 2 && (
                  <button type="button" onClick={() => setLegs((ls) => ls.filter((_, j) => j !== i))}
                    className="text-zinc-400 hover:text-rose-600" title="Remove leg" data-testid={`gf-leg-remove-${i}`}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {legs.length < 5 && (
            <button type="button" onClick={() => setLegs((ls) => [...ls, { from: "", to: "", date: "" }])}
              className="inline-flex items-center gap-1 text-xs font-700 text-blue-700 hover:underline" data-testid="gf-add-leg">
              <Plus className="w-3.5 h-3.5" /> Add another leg
            </button>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2.5 mt-2.5">
        <select value={f.cabin} onChange={(e) => set("cabin", e.target.value)} className={`${inp2} lg:col-span-2`} data-testid="gf-cabin">
          <option value="economy">Economy</option><option value="premium economy">Premium Economy</option>
          <option value="business">Business</option><option value="first">First</option>
        </select>
        <select value={f.passengers} onChange={(e) => set("passengers", Number(e.target.value))} className={inp2} data-testid="gf-passengers">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{`${n} traveler${n > 1 ? "s" : ""}`}</option>)}
        </select>
        <button type="submit" data-testid="gf-search-btn"
          className="lg:col-span-3 inline-flex items-center justify-center gap-2 bg-blue-700 text-white px-5 py-2.5 text-sm font-700 hover:bg-blue-800 transition-colors">
          Search on Google Flights <ExternalLink className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
        <ArrowLeftRight className="w-3 h-3" />
        {type === "multi"
          ? "Opens Google Flights with your legs — double-check them there before booking."
          : "Opens google.com/travel/flights in a new tab with your search pre-filled."}
      </p>
      {type !== "multi" && <SaveRouteToTrip from={f.from} to={f.to} depart={f.depart} ret={f.ret} type={type} />}
    </form>
  );
};
